
import { useNodegroupConfig } from "@/hooks/config/useNodegroupConfig";
import { useHadoopConfig } from "@/hooks/config/useHadoopConfig";
import { useMySQLConfig } from "@/hooks/config/useMySQLConfig";
import { useVIAConfig } from "@/hooks/config/useVIAConfig";
import { useAIOpsConfig } from "@/hooks/config/useAIOpsConfig";
import { useTranslation } from "react-i18next";
import {
  getNodegroupTemplate,
  getHadoopTemplate,
  getMySQLTemplate,
  getVIATemplate,
  getAIOpsTemplate,
  empty,
  getDruidTemplate,
  getGiteaTemplate,
} from "@/components/instance/config/ServerToClientConverter";
import { useDruidConfig } from "./useDruidConfig";
import { useGiteaConfig } from "./useGiteaConfig";
import { requiredRule } from "./useBasicComponent";
import { debounce } from "lodash";
import { useEffect, useState, useCallback } from "react";
import { useResourceContext } from "@/layouts/ResourceContext";
import { notification, Input } from "antd";
import WrappedSelect from "@/components/instance/config/WrappedSelect";
import { getInstanceTemplate } from "@/apis";

export const useProfileWizardConfig = ({
  action="create",
  formRef,
  onValuesChangeCallback,
  version = "",
  enableDruid = true,
  enableGitea = true
}) => {
  const { t } = useTranslation();
  const generalConfig = useGeneralConfig({formRef, action, onValuesChangeCallback });
  const nodegroupConfig = useNodegroupConfig(enableDruid);
  const hadoopConfig = useHadoopConfig();
  const mySQLConfig = useMySQLConfig();
  const viaConfig = useVIAConfig();
  const aiopsConfig = useAIOpsConfig(version);
  const druidConfig = useDruidConfig();
  const giteaConfig = useGiteaConfig();


  const columns = [
    generalConfig,
    nodegroupConfig,
    hadoopConfig,
    mySQLConfig,
    viaConfig,
    aiopsConfig,
  ];
  const template = [
    empty,
    getNodegroupTemplate,
    getHadoopTemplate,
    getMySQLTemplate,
    getVIATemplate,
    getAIOpsTemplate,
  ];

  const steps = [
    t("instances.wizard.tab.general", "General"),
    t("instances.wizard.tab.nodegroup", "Nodegroup"),
    t("instances.wizard.tab.hadoop", "Hadoop"),
    t("instances.wizard.tab.mysql", "MySQL"),
    t("instances.wizard.tab.via", "VIA"),
    t("instances.wizard.tab.aiops", "AIOps"),
  ].map((title) => ({ title }));

  if(enableDruid) {
    columns.splice(4, 0, druidConfig);
    template.splice(4, 0, getDruidTemplate);
    steps.splice(4, 0, {"title": t("instances.wizard.tab.druid", "Druid")});
  }

  // push, not splice: columns/template/steps are indexed together by the current step number,
  // and a second fixed-index splice would make the order depend on which one ran first.
  if(enableGitea) {
    columns.push(giteaConfig);
    template.push(getGiteaTemplate);
    steps.push({"title": t("instances.wizard.tab.gitea", "Gitea")});
  }

  return {
    steps,
    columns,
    template,
  };
};

const useGeneralConfig = ({ formRef, action, onValuesChangeCallback }) => {
  const { t } = useTranslation();

  const [templateLoading, setTemplateLoading] = useState(false);
  const [templateEnum, setTemplateEnum] = useState({});
  const [templateDisabled, setTemplateDisabled] = useState(false);
  const [validateLoading, setValidateLoading] = useState(false);

  const {
    rootState: { eks },
  } = useResourceContext();

  const debouncedValidate = useCallback(
    debounce(async (form, dataIndex) => {
      console.log("validation :");
      await form.validateFields([dataIndex]);
    }, 1000),
    [],
  );

  useEffect(() => {
    const fetchProfile = async () => {
      const tpltEnum = eks.data["instance-profiles"].reduce(
        (accumulator, item) => {
          accumulator[item] = { text: item };
          return accumulator;
        },
        {},
      );
      setTemplateEnum(tpltEnum);
      setTemplateLoading(false);
    };

    fetchProfile();
  }, []);

  useEffect(() => {
    const f = async () => await loadTemplate("normal");
    if(action == "create") f();
  }, []);

  const loadTemplate = async (templateType) => {
    setTemplateLoading(true);
    setTemplateDisabled(true);
    formRef.current.setFieldsValue({
      ["template"]: templateType,
    });

    const data = await getInstanceTemplate(templateType);
    formRef.current.setFieldsValue({
      ["template-define"]: data,
    });

    if (data.status !== "OK") {
      notification.error({
        description: t(
          "settings.profiles.error.profileLoading",
          "Fail to load profile from server. Please re-select profile later.",
        ),
      });
    }
    setTemplateLoading(false);
    setTemplateDisabled(false);
  };

  const rawSchema = [
    {
      title: t("settings.profile.form.name", "Profile Name"),
      dataIndex: "name",
      renderFormItem: (item, _, form) => {
        return (
          <Input disabled={action != "create"} />
        );
      },
      width: "400px",
    },
    {
      title: t("settings.profile.form.desc", "Profile Descrition"),
      dataIndex: "desc",
      width: "400px",
      valueType: "textarea",
    }
  ];
    if(action =="create"){
      rawSchema[0].formItemProps= {
        rules: [...requiredRule(t),
        ({ getFieldValue }) => ({
          async validator(_, value) {
            const value1 = getFieldValue("name");

            if (Object.keys(templateEnum).indexOf(value1) > -1) {
              return Promise.reject(
                new Error(
                  t(
                    "settings.profiles.error.dupName",
                    "Name exists. Please input another name.",
                  ),
                ),
              );
            }
            return Promise.resolve();
            
          },
        }),
      ],
      };
      rawSchema.splice(1,0,    {
        i18nTitle: "settings.profiles.form.template",
        title: t("settings.profiles.form.template", "Profile as Template"),
        dataIndex: "template",
        renderFormItem: (item, _, form) => {
          return (
            <WrappedSelect
              width="400px"
              defaultValue="normal"
              options={templateEnum}
              loading={templateLoading}
              disabled={templateDisabled}
              onChange={async (newVariables) => {
                await loadTemplate(newVariables);
                onValuesChangeCallback({ template: newVariables });
              }}
            />
          );
        },
        formItemProps: {
          rules: [
            //...requiredRule(t),
            ({ getFieldValue }) => ({
              async validator(_, value) {
                const value1 = getFieldValue("template-define");
                if (value1 !== undefined && value1.status !== "OK") {
                  Promise.reject(
                    new Error(
                      t(
                        "settings.profiles.error.profileLoading",
                        "Fail to load profile from server. Please re-select profile later.",
                      ),
                    ),
                  );
                }
                return Promise.resolve();
              },
            }),
          ],
        },
        width: "400px",
      })
    }


  return rawSchema;
};

